import { h, Fragment } from "preact";
import { useState, useRef } from "preact/hooks";
import { Trans } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import { createBuyOffer, createSellOffer } from "../../../../services/p2p-offers";
import User from "../../../../services/user";
import formatCurrency from "../../../../services/currency";
import { CircleCheck, CircleXmark } from "../../utils/icons";
import { shouldAskTelegram, TelegramButton } from "../../../../services/telegram/openidLogin";
import Config from "../../config";

type Step = 'amount' | 'loading' | 'success' | 'error';

const ERROR_LABELS: Record<string, ReturnType<typeof msg>> = {
    P2P_BUY_FAILED: msg`Failed to create deposit request. Please try again.`,
    P2P_SELL_FAILED: msg`Failed to create withdrawal request. Please try again.`,
    P2P_INSUFFICIENT_FUNDS: msg`Insufficient funds.`,
};

function translateError(code: string): string {
    const label = ERROR_LABELS[code];
    return label ? window.Translation(label) : code;
}

interface TelegramDepositPageProps {
    processor: string;
    provider: string;
    name: string;
}

export function TelegramDepositPage(props: TelegramDepositPageProps) {
    const [step, setStep] = useState<Step>('amount');
    const [offerId, setOfferId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const form = useRef<HTMLFormElement>();

    const requestNotifications = shouldAskTelegram();

    if (requestNotifications) {
        return <div class="h-full w-full min-h-[inherit] flex items-center justify-center">
            <TelegramButton
                clientId={Config.telegramLogin}
                onError={console.error}
                onSuccess={console.log}
                class=""
                iconOnly={false}
                shape="square"
                text={window.Translation(msg`Connect to enable P2P`)}
            />
        </div>
    }

    const handleSubmit = async (e: Event) => {
        e.preventDefault();
        const amount = parseFloat(new FormData(form.current).get('amount').toString());
        if (isNaN(amount) || amount <= 0) return;

        setStep('loading');
        try {
            const result = await createBuyOffer(amount);
            setOfferId(result.buyOfferId);
            setStep('success');
        } catch (err: any) {
            const raw = err?.error;
            setError(translateError(typeof raw === 'string' ? raw : raw?.message || err?.message || ''));
            setStep('error');
        }
    };

    return (
        <div class="grow w-full flex flex-col items-center text-slate-800 bg-linear-to-br from-fuchsia-300 to-blue-300 overflow-auto">
            {step === 'amount' && (
                <form
                    class="flex flex-col items-center gap-2 w-full max-w-sm p-4"
                    ref={form}
                    onSubmit={handleSubmit}
                >
                    <div class="w-full text-4xl font-light pb-2">
                        <Trans>Deposit via Telegram</Trans>
                    </div>
                    <div class="w-full">
                        <div class="text-sm"><Trans>Amount</Trans></div>
                        <div class="h-8 w-full flex">
                            <input class="border h-full rounded-s bg-slate-100 border-slate-300 text-slate-800 py-2 px-3 w-16 text-base" disabled={true} value="ETB" />
                            <input class="border border-l-0 h-full rounded-e bg-white border-slate-300 text-slate-950 py-2 px-3 text-base grow"
                                required={true}
                                name="amount"
                                type="number"
                                step="1"
                                min="10"
                                placeholder="Enter Amount Here"
                            />
                        </div>
                    </div>
                    <div class="w-full py-2 flex justify-center">
                        <input type="submit" value="Deposit" class="mx-auto px-5 py-3 text-center rounded bg-emerald-600 text-slate-50 border-emerald-700 text-xl" />
                    </div>
                </form>
            )}

            {step === 'loading' && (
                <div class="flex items-center justify-center h-48">
                    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
                </div>
            )}

            {step === 'success' && (
                <div class="flex flex-col items-center gap-4 p-6 text-center max-w-sm">
                    <CircleCheck class="h-32 text-green-500" />
                    <div class="text-xl font-bold"><Trans>Deposit Request Sent</Trans></div>
                    <div class="text-base">
                        <Trans>Your deposit request has been submitted. Contact a cashier via Telegram to complete the payment.</Trans>
                    </div>
                    <div class="bg-white rounded p-3 w-full">
                        <div class="text-sm text-slate-500"><Trans>Reference ID</Trans></div>
                        <div class="text-lg font-mono font-bold text-slate-900">{offerId}</div>
                    </div>
                    <button
                        class="px-5 py-3 rounded bg-emerald-600 text-slate-50 text-xl"
                        onClick={() => { setStep('amount'); setOfferId(null); setError(null); }}
                    >
                        <Trans>New Deposit</Trans>
                    </button>
                </div>
            )}

            {step === 'error' && (
                <div class="flex flex-col items-center gap-4 p-6 text-center max-w-sm">
                    <CircleXmark class="h-32 text-red-600" />
                    <div class="text-xl font-bold"><Trans>Error</Trans></div>
                    <div class="text-base text-red-600">{error}</div>
                    <button
                        class="px-5 py-3 rounded bg-emerald-600 text-slate-50 text-xl"
                        onClick={() => { setStep('amount'); setError(null); }}
                    >
                        <Trans>Try Again</Trans>
                    </button>
                </div>
            )}
        </div>
    );
}

interface TelegramWithdrawPageProps {
    processor: string;
    provider: string;
    name: string;
}

export function TelegramWithdrawPage(props: TelegramWithdrawPageProps) {
    const [step, setStep] = useState<Step>('amount');
    const [offerId, setOfferId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const form = useRef<HTMLFormElement>();

    const requestNotifications = shouldAskTelegram();

    if (requestNotifications) {
        return <div class="h-full w-full min-h-[inherit] flex items-center justify-center">
            <TelegramButton
                clientId={Config.telegramLogin}
                onError={console.error}
                onSuccess={console.log}
                class=""
                iconOnly={false}
                shape="square"
                text={window.Translation(msg`Connect to enable P2P`)}
            />
        </div>
    }

    const handleSubmit = async (e: Event) => {
        e.preventDefault();
        const amount = parseFloat(new FormData(form.current).get('amount').toString());
        if (isNaN(amount) || amount <= 0) return;

        setStep('loading');
        try {
            const result = await createSellOffer(amount);
            setOfferId(result.offerId);
            setStep('success');
        } catch (err: any) {
            const raw = err?.error;
            setError(translateError(typeof raw === 'string' ? raw : raw?.message || err?.message || ''));
            setStep('error');
        }
    };

    return (
        <div class="grow w-full flex flex-col items-center text-slate-800 bg-linear-to-br from-fuchsia-300 to-blue-300 overflow-auto">
            {step === 'amount' && (
                <form
                    class="flex flex-col items-center gap-2 w-full max-w-sm p-4"
                    ref={form}
                    onSubmit={handleSubmit}
                >
                    <div class="w-full text-4xl font-light pb-2">
                        <Trans>Withdraw via Telegram</Trans>
                    </div>
                    <div class="w-full">
                        <div class="flex justify-between text-sm">
                            <span><Trans>Amount</Trans></span>
                            <span class="text-slate-500">
                                <Trans>Max</Trans>: {formatCurrency(User.payable())}
                            </span>
                        </div>
                        <div class="h-8 w-full flex">
                            <input class="border h-full rounded-s bg-slate-100 border-slate-300 text-slate-800 py-2 px-3 w-16 text-base" disabled={true} value="ETB" />
                            <input class="border border-l-0 h-full rounded-e bg-white border-slate-300 text-slate-950 py-2 px-3 text-base grow"
                                required={true}
                                name="amount"
                                type="number"
                                step="1"
                                min="10"
                                max={User.payable()}
                                placeholder="Enter Amount Here"
                            />
                        </div>
                    </div>
                    <div class="w-full py-2 flex justify-center">
                        <input type="submit" value="Withdraw" class="mx-auto px-5 py-3 text-center rounded bg-emerald-600 text-slate-50 border-emerald-700 text-xl" />
                    </div>
                </form>
            )}

            {step === 'loading' && (
                <div class="flex items-center justify-center h-48">
                    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
                </div>
            )}

            {step === 'success' && (
                <div class="flex flex-col items-center gap-4 p-6 text-center max-w-sm">
                    <CircleCheck class="h-32 text-green-500" />
                    <div class="text-xl font-bold"><Trans>Withdraw Request Sent</Trans></div>
                    <div class="text-base">
                        <Trans>Your withdrawal request has been submitted. Contact a cashier via Telegram to complete the process.</Trans>
                    </div>
                    <div class="bg-white rounded p-3 w-full">
                        <div class="text-sm text-slate-500"><Trans>Reference ID</Trans></div>
                        <div class="text-lg font-mono font-bold text-slate-900">{offerId}</div>
                    </div>
                    <button
                        class="px-5 py-3 rounded bg-emerald-600 text-slate-50 text-xl"
                        onClick={() => { setStep('amount'); setOfferId(null); setError(null); }}
                    >
                        <Trans>New Withdraw</Trans>
                    </button>
                </div>
            )}

            {step === 'error' && (
                <div class="flex flex-col items-center gap-4 p-6 text-center max-w-sm">
                    <CircleXmark class="h-32 text-red-600" />
                    <div class="text-xl font-bold"><Trans>Error</Trans></div>
                    <div class="text-base text-red-600">{error}</div>
                    <button
                        class="px-5 py-3 rounded bg-emerald-600 text-slate-50 text-xl"
                        onClick={() => { setStep('amount'); setError(null); }}
                    >
                        <Trans>Try Again</Trans>
                    </button>
                </div>
            )}
        </div>
    );
}
